import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { systemAPI } from '../utils/api';
import { 
  Settings, 
  Building2, 
  Mail, 
  Bell, 
  Shield, 
  Globe, 
  CreditCard,
  FileText,
  Clock,
  Save,
  CheckCircle,
  Plug,
  Eye,
  EyeOff,
  TestTube,
  Loader2,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SETTING_SECTIONS = [
  { id: 'company', label: 'Company Info', icon: Building2 },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'email', label: 'Email Settings', icon: Mail },
  { id: 'documents', label: 'Document Settings', icon: FileText },
  { id: 'security', label: 'Security', icon: Shield },
];

export default function SettingsPage() {
  const [activeSection, setActiveSection] = useState('company');
  const [saving, setSaving] = useState(false);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingZohoSign, setTestingZohoSign] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showZohoSecret, setShowZohoSecret] = useState(false);
  const [showRefreshToken, setShowRefreshToken] = useState(false);
  
  const [settings, setSettings] = useState({
    // Company Info
    company_name: 'Immiways Migration Advisory Services Pvt Ltd',
    company_email: 'info@immiways.com',
    company_phone: '+91 98765 43210',
    company_address: 'Mumbai, Maharashtra, India',
    gst_number: '',
    
    // Notifications
    notify_new_client: true,
    notify_document_upload: true,
    notify_case_update: true,
    notify_complaint: true,
    reminder_days_before_deadline: 5,
    
    // Email Settings
    email_sender_name: 'Immiways',
    email_footer_text: 'Immiways Migration Advisory Services',
    auto_send_welcome_email: true,
    auto_send_reminder_emails: true,
    
    // Document Settings
    max_file_size_mb: 10,
    allowed_file_types: 'pdf,jpg,jpeg,png,doc,docx',
    document_deadline_days: 15,
    
    // Security
    session_timeout_minutes: 30,
    require_strong_password: true,
    enable_2fa: false,
  });

  // Integration settings (separate state for security)
  const [integrationSettings, setIntegrationSettings] = useState({
    zoho_smtp_host: 'smtp.zoho.in',
    zoho_smtp_port: 465,
    zoho_smtp_user: 'processteam@immiwaysmigration.in',
    zoho_smtp_password: '',
    zoho_smtp_password_set: false,
    email_sender: 'processteam@immiwaysmigration.in',
    email_sender_name: 'Immiways Migration Advisory Services',
    email_throttle_per_minute: 3,
    email_max_retries: 3,
    zoho_sign_client_id: '',
    zoho_sign_client_id_set: false,
    zoho_sign_client_secret: '',
    zoho_sign_client_secret_set: false,
    zoho_sign_refresh_token: '',
    zoho_sign_refresh_token_set: false,
    reminder_time_ist: '09:00',
    smtp_configured: false,
    zoho_sign_configured: false,
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    if (activeSection === 'integrations') {
      fetchIntegrationSettings();
    }
  }, [activeSection]);

  const fetchCompanySettings = async () => {
    try {
      const res = await systemAPI.getCompanySettings();
      if (res.data && Object.keys(res.data).length > 0) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch {
      // silently fall back to defaults
    }
  };

  const fetchIntegrationSettings = async () => {
    try {
      setLoadingIntegrations(true);
      const response = await systemAPI.getIntegrationSettings();
      setIntegrationSettings(prev => ({
        ...prev,
        ...response.data,
        // Don't overwrite password fields if they're just status flags
        zoho_smtp_password: '',
        zoho_sign_client_id: response.data.zoho_sign_client_id_set ? '' : '',
        zoho_sign_client_secret: '',
        zoho_sign_refresh_token: '',
      }));
    } catch (error) {
      console.error('Error fetching integration settings:', error);
      toast.error('Failed to load integration settings');
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await systemAPI.updateCompanySettings(settings);
      toast.success('Settings saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIntegrations = async () => {
    try {
      setSaving(true);
      
      // Build update object with only changed fields
      const updateData = {};
      
      if (integrationSettings.zoho_smtp_host) updateData.zoho_smtp_host = integrationSettings.zoho_smtp_host;
      if (integrationSettings.zoho_smtp_port) updateData.zoho_smtp_port = integrationSettings.zoho_smtp_port;
      if (integrationSettings.zoho_smtp_user) updateData.zoho_smtp_user = integrationSettings.zoho_smtp_user;
      if (integrationSettings.zoho_smtp_password) updateData.zoho_smtp_password = integrationSettings.zoho_smtp_password;
      if (integrationSettings.email_sender) updateData.email_sender = integrationSettings.email_sender;
      if (integrationSettings.email_sender_name) updateData.email_sender_name = integrationSettings.email_sender_name;
      if (integrationSettings.email_throttle_per_minute) updateData.email_throttle_per_minute = integrationSettings.email_throttle_per_minute;
      if (integrationSettings.email_max_retries) updateData.email_max_retries = integrationSettings.email_max_retries;
      if (integrationSettings.zoho_sign_client_id) updateData.zoho_sign_client_id = integrationSettings.zoho_sign_client_id;
      if (integrationSettings.zoho_sign_client_secret) updateData.zoho_sign_client_secret = integrationSettings.zoho_sign_client_secret;
      if (integrationSettings.zoho_sign_refresh_token) updateData.zoho_sign_refresh_token = integrationSettings.zoho_sign_refresh_token;
      if (integrationSettings.reminder_time_ist) updateData.reminder_time_ist = integrationSettings.reminder_time_ist;
      
      await systemAPI.updateIntegrationSettings(updateData);
      toast.success('Integration settings saved successfully!');
      
      // Refresh settings to get updated status
      await fetchIntegrationSettings();
    } catch (error) {
      console.error('Error saving integration settings:', error);
      toast.error('Failed to save integration settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    try {
      setTestingSmtp(true);
      const response = await systemAPI.testSmtp();
      if (response.data.status === 'success') {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to test SMTP connection');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestZohoSign = async () => {
    try {
      setTestingZohoSign(true);
      const response = await systemAPI.testZohoSign();
      if (response.data.status === 'success') {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to test Zoho Sign connection');
    } finally {
      setTestingZohoSign(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="settings-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="text-teal-500" size={32} />
            Platform Settings
          </h1>
          <p className="text-slate-600">Configure your immigration consultancy platform</p>
        </div>

        <div className="grid grid-cols-4 gap-6">
          {/* Settings Navigation */}
          <div className="col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <nav className="space-y-1">
                {SETTING_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                        activeSection === section.id
                          ? 'bg-teal-50 text-teal-700 border border-teal-200'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {/* Company Info */}
              {activeSection === 'company' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Building2 className="text-teal-500" size={24} />
                    Company Information
                  </h2>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                      <input
                        type="text"
                        value={settings.company_name}
                        onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company Email</label>
                      <input
                        type="email"
                        value={settings.company_email}
                        onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={settings.company_phone}
                        onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
                      <input
                        type="text"
                        value={settings.gst_number}
                        onChange={(e) => setSettings({ ...settings, gst_number: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                        placeholder="Enter GST number"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company Address</label>
                      <textarea
                        value={settings.company_address}
                        onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none resize-none"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Integrations */}
              {activeSection === 'integrations' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Plug className="text-teal-500" size={24} />
                    Integration Settings
                  </h2>
                  
                  {loadingIntegrations ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                    </div>
                  ) : (
                    <>
                      {/* Zoho Mail SMTP Section */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            <Mail size={20} className="text-blue-500" />
                            Zoho Mail SMTP
                          </h3>
                          {integrationSettings.smtp_configured ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle size={16} />
                              Connected
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-amber-600 text-sm">
                              <AlertCircle size={16} />
                              Not Configured
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Host</label>
                            <input
                              type="text"
                              value={integrationSettings.zoho_smtp_host}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, zoho_smtp_host: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                              placeholder="smtp.zoho.in"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Port</label>
                            <input
                              type="number"
                              value={integrationSettings.zoho_smtp_port}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, zoho_smtp_port: parseInt(e.target.value) })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                              placeholder="465"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Username</label>
                            <input
                              type="email"
                              value={integrationSettings.zoho_smtp_user}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, zoho_smtp_user: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                              placeholder="processteam@immiwaysmigration.in"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              SMTP Password {integrationSettings.zoho_smtp_password_set && <span className="text-green-600">(Set)</span>}
                            </label>
                            <div className="relative">
                              <input
                                type={showSmtpPassword ? "text" : "password"}
                                value={integrationSettings.zoho_smtp_password}
                                onChange={(e) => setIntegrationSettings({ ...integrationSettings, zoho_smtp_password: e.target.value })}
                                className="w-full px-4 py-2 pr-10 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                                placeholder={integrationSettings.zoho_smtp_password_set ? "••••••••" : "App-specific password"}
                              />
                              <button
                                type="button"
                                onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showSmtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Sender Email</label>
                            <input
                              type="email"
                              value={integrationSettings.email_sender}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, email_sender: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Sender Name</label>
                            <input
                              type="text"
                              value={integrationSettings.email_sender_name}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, email_sender_name: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Emails per Minute (Throttle)</label>
                            <input
                              type="number"
                              value={integrationSettings.email_throttle_per_minute}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, email_throttle_per_minute: parseInt(e.target.value) })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                              min={1}
                              max={10}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Max Retry Attempts</label>
                            <input
                              type="number"
                              value={integrationSettings.email_max_retries}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, email_max_retries: parseInt(e.target.value) })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                              min={1}
                              max={5}
                            />
                          </div>
                        </div>
                        
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={handleTestSmtp}
                            disabled={testingSmtp}
                            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition-all disabled:opacity-50 flex items-center gap-2"
                          >
                            {testingSmtp ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <TestTube size={18} />
                            )}
                            Test Connection
                          </button>
                        </div>
                      </div>
                      
                      {/* Zoho Sign Section */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            <FileText size={20} className="text-purple-500" />
                            Zoho Sign (E-Signatures)
                          </h3>
                          {integrationSettings.zoho_sign_configured ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle size={16} />
                              Connected
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-amber-600 text-sm">
                              <AlertCircle size={16} />
                              Not Configured
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-slate-500 mb-4">
                          Get API credentials from <a href="https://api-console.zoho.in/" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">Zoho API Console</a>
                        </p>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Client ID {integrationSettings.zoho_sign_client_id_set && <span className="text-green-600">(Set)</span>}
                            </label>
                            <input
                              type="text"
                              value={integrationSettings.zoho_sign_client_id}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, zoho_sign_client_id: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                              placeholder={integrationSettings.zoho_sign_client_id_set ? "••••••••••••" : "Enter Client ID"}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Client Secret {integrationSettings.zoho_sign_client_secret_set && <span className="text-green-600">(Set)</span>}
                            </label>
                            <div className="relative">
                              <input
                                type={showZohoSecret ? "text" : "password"}
                                value={integrationSettings.zoho_sign_client_secret}
                                onChange={(e) => setIntegrationSettings({ ...integrationSettings, zoho_sign_client_secret: e.target.value })}
                                className="w-full px-4 py-2 pr-10 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                                placeholder={integrationSettings.zoho_sign_client_secret_set ? "••••••••••••" : "Enter Client Secret"}
                              />
                              <button
                                type="button"
                                onClick={() => setShowZohoSecret(!showZohoSecret)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showZohoSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Refresh Token {integrationSettings.zoho_sign_refresh_token_set && <span className="text-green-600">(Set)</span>}
                            </label>
                            <div className="relative">
                              <input
                                type={showRefreshToken ? "text" : "password"}
                                value={integrationSettings.zoho_sign_refresh_token}
                                onChange={(e) => setIntegrationSettings({ ...integrationSettings, zoho_sign_refresh_token: e.target.value })}
                                className="w-full px-4 py-2 pr-10 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                                placeholder={integrationSettings.zoho_sign_refresh_token_set ? "••••••••••••" : "Enter Refresh Token"}
                              />
                              <button
                                type="button"
                                onClick={() => setShowRefreshToken(!showRefreshToken)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showRefreshToken ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={handleTestZohoSign}
                            disabled={testingZohoSign}
                            className="px-4 py-2 bg-purple-50 text-purple-600 rounded-lg font-medium hover:bg-purple-100 transition-all disabled:opacity-50 flex items-center gap-2"
                          >
                            {testingZohoSign ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <TestTube size={18} />
                            )}
                            Test Connection
                          </button>
                        </div>
                      </div>
                      
                      {/* General Settings */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                          <Clock size={20} className="text-teal-500" />
                          Automation Settings
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Daily Reminder Time (IST)</label>
                            <input
                              type="time"
                              value={integrationSettings.reminder_time_ist}
                              onChange={(e) => setIntegrationSettings({ ...integrationSettings, reminder_time_ist: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                            />
                            <p className="text-xs text-slate-500 mt-1">Time to send daily document reminder emails</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Save Button for Integrations */}
                      <div className="flex justify-end">
                        <button
                          onClick={handleSaveIntegrations}
                          disabled={saving}
                          className="px-6 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={18} />
                              Save Integration Settings
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Notifications */}
              {activeSection === 'notifications' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Bell className="text-teal-500" size={24} />
                    Notification Settings
                  </h2>
                  
                  <div className="space-y-4">
                    {[
                      { key: 'notify_new_client', label: 'New client registration' },
                      { key: 'notify_document_upload', label: 'Document uploads' },
                      { key: 'notify_case_update', label: 'Case status updates' },
                      { key: 'notify_complaint', label: 'New complaints' },
                    ].map((item) => (
                      <label key={item.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                        <span className="font-medium text-slate-700">{item.label}</span>
                        <input
                          type="checkbox"
                          checked={settings[item.key]}
                          onChange={(e) => setSettings({ ...settings, [item.key]: e.target.checked })}
                          className="w-5 h-5 text-teal-500 border-slate-300 rounded focus:ring-teal-500"
                        />
                      </label>
                    ))}
                    
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Send reminder X days before deadline
                      </label>
                      <input
                        type="number"
                        value={settings.reminder_days_before_deadline}
                        onChange={(e) => setSettings({ ...settings, reminder_days_before_deadline: parseInt(e.target.value) })}
                        className="w-24 px-4 py-2 border border-slate-200 rounded-lg"
                        min={1}
                        max={30}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Email Settings */}
              {activeSection === 'email' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Mail className="text-teal-500" size={24} />
                    Email Settings
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Sender Name</label>
                      <input
                        type="text"
                        value={settings.email_sender_name}
                        onChange={(e) => setSettings({ ...settings, email_sender_name: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email Footer Text</label>
                      <input
                        type="text"
                        value={settings.email_footer_text}
                        onChange={(e) => setSettings({ ...settings, email_footer_text: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      />
                    </div>
                    
                    <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                      <span className="font-medium text-slate-700">Auto-send welcome emails to new clients</span>
                      <input
                        type="checkbox"
                        checked={settings.auto_send_welcome_email}
                        onChange={(e) => setSettings({ ...settings, auto_send_welcome_email: e.target.checked })}
                        className="w-5 h-5 text-teal-500 border-slate-300 rounded focus:ring-teal-500"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                      <span className="font-medium text-slate-700">Auto-send document reminder emails</span>
                      <input
                        type="checkbox"
                        checked={settings.auto_send_reminder_emails}
                        onChange={(e) => setSettings({ ...settings, auto_send_reminder_emails: e.target.checked })}
                        className="w-5 h-5 text-teal-500 border-slate-300 rounded focus:ring-teal-500"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Document Settings */}
              {activeSection === 'documents' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <FileText className="text-teal-500" size={24} />
                    Document Settings
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Maximum File Size (MB)</label>
                      <input
                        type="number"
                        value={settings.max_file_size_mb}
                        onChange={(e) => setSettings({ ...settings, max_file_size_mb: parseInt(e.target.value) })}
                        className="w-32 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                        min={1}
                        max={50}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Allowed File Types</label>
                      <input
                        type="text"
                        value={settings.allowed_file_types}
                        onChange={(e) => setSettings({ ...settings, allowed_file_types: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                        placeholder="pdf,jpg,png,doc"
                      />
                      <p className="text-xs text-slate-500 mt-1">Comma-separated list of allowed extensions</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Document Submission Deadline (Days)</label>
                      <input
                        type="number"
                        value={settings.document_deadline_days}
                        onChange={(e) => setSettings({ ...settings, document_deadline_days: parseInt(e.target.value) })}
                        className="w-32 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                        min={1}
                        max={90}
                      />
                      <p className="text-xs text-slate-500 mt-1">Default number of days for clients to submit documents</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Security */}
              {activeSection === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Shield className="text-teal-500" size={24} />
                    Security Settings
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Session Timeout (Minutes)</label>
                      <input
                        type="number"
                        value={settings.session_timeout_minutes}
                        onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) })}
                        className="w-32 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                        min={5}
                        max={120}
                      />
                    </div>
                    
                    <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                      <div>
                        <span className="font-medium text-slate-700">Require strong passwords</span>
                        <p className="text-xs text-slate-500">Minimum 8 characters, uppercase, lowercase, numbers</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.require_strong_password}
                        onChange={(e) => setSettings({ ...settings, require_strong_password: e.target.checked })}
                        className="w-5 h-5 text-teal-500 border-slate-300 rounded focus:ring-teal-500"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                      <div>
                        <span className="font-medium text-slate-700">Enable Two-Factor Authentication</span>
                        <p className="text-xs text-slate-500">Require 2FA for all admin users</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.enable_2fa}
                        onChange={(e) => setSettings({ ...settings, enable_2fa: e.target.checked })}
                        className="w-5 h-5 text-teal-500 border-slate-300 rounded focus:ring-teal-500"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-6 border-t border-slate-200 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
